import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { MapPin, Pencil, Package, Zap } from 'lucide-react';
import { SheetShell } from '../../components/boothly/sheet-shell.jsx';
import { InsetGroup, InsetRow } from '../../components/boothly/inset-list.jsx';
import { fmtCurrency } from '../../lib/format.js';
import { SaleNumpad } from './sale-numpad.jsx';

const IOS_EASE = 'cubic-bezier(0.32,0.72,0,1)';

export function MarktagDetailSheet({ market, open, onOpenChange, onUpdateBoothFee, dark, storeId, authToken }) {
  const [fullSales, setFullSales] = useState(null);
  const [loading, setLoading] = useState(false);
  // Standgebühr lokal halten (market = Snapshot) → optimistisches Re-render nach Edit.
  const [boothFee, setBoothFee] = useState(null);    // committed/angezeigter Wert (null = keine)
  const [feeEditOpen, setFeeEditOpen] = useState(false);
  const [draftFee, setDraftFee] = useState(0);       // laufender NumPad-Wert beim Bearbeiten
  // Eingefrorener NumPad-Seed: nur beim Öffnen gesetzt, NICHT beim Tippen — sonst flösse jeder
  // Tastendruck über prefill zurück und re-seedet das NumPad (Single-Digit-Bug).
  const [feeSeed, setFeeSeed] = useState(0);

  // NumPad-Footer (Pattern aus neuer-markttag-sheet.jsx): Slide-in + max-height-Kollaps,
  // Wert läuft per hideDisplay live in der Standgebühr-Zeile mit (Settings-Kontext, kein Verkauf).
  const [closingPad, setClosingPad] = useState(false);
  const [padEntered, setPadEntered] = useState(false);
  const [padH, setPadH]             = useState(0);
  const [padSession, setPadSession] = useState(0);  // Mount-Key → frischer Seed pro Öffnen
  const contentRef      = useRef(null);
  const footerRef       = useRef(null);
  const scrollTargetRef = useRef(null);

  // Beim Öffnen / Marktwechsel die Standgebühr aus dem Snapshot übernehmen.
  useEffect(() => {
    if (open) {
      setBoothFee(market?.booth_fee ?? null);
      setFeeEditOpen(false);
      setClosingPad(false);
      setPadEntered(false);
    }
  }, [open, market?.id, market?.booth_fee]);

  // Öffnen: im nächsten Frame Transform-Slide ausfahren (translateY 100%→0).
  useEffect(() => {
    if (feeEditOpen) {
      const r = requestAnimationFrame(() => setPadEntered(true));
      return () => cancelAnimationFrame(r);
    }
  }, [feeEditOpen]);

  // NumPad-Inhaltshöhe messen → sauberer max-height-Kollaps beim Schließen.
  useLayoutEffect(() => {
    if (feeEditOpen && contentRef.current) {
      const h = contentRef.current.scrollHeight;
      if (h && h !== padH) setPadH(h);
    }
  }, [feeEditOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Standgebühr-Karte beim Öffnen in Sicht scrollen (deterministisch via transitionend des Footers).
  useEffect(() => {
    if (!feeEditOpen) return;
    const el = scrollTargetRef.current;
    const footer = footerRef.current;
    if (!el) return;
    let done = false;
    const doScroll = () => { if (done) return; done = true; el.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); };
    const onEnd = (e) => { if (e.target === footer) doScroll(); };
    footer?.addEventListener('transitionend', onEnd);
    const t = setTimeout(doScroll, 380);
    return () => { footer?.removeEventListener('transitionend', onEnd); clearTimeout(t); };
  }, [feeEditOpen]);

  // Fetch full session detail when sheet opens (unless sale_items already embedded)
  useEffect(() => {
    if (!open || !market?.id) { setFullSales(null); return; }
    // If first sale already has sale_items, data is pre-fetched (e.g. from seed) — no network call needed
    const firstSale = (Array.isArray(market.sales) ? market.sales : [])[0];
    if (firstSale?.sale_items) {
      setFullSales(market.sales);
      return;
    }
    if (!storeId || !authToken) return;
    setLoading(true);
    fetch(`/api/stores/${storeId}/sessions/${market.id}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => { setFullSales(data?.sales ?? null); })
      .catch(() => { setFullSales(null); })
      .finally(() => setLoading(false));
  }, [open, market?.id, storeId, authToken]);

  if (!open || !market) return null;

  // Derive display sales — prefer fetched detail, fall back to list-endpoint data
  const displaySales = (fullSales ?? (Array.isArray(market.sales) ? market.sales : []))
    .filter(s => !s.is_voided);

  // ── Stats ──────────────────────────────────────────────────────────────────
  const totalRevenue = displaySales.reduce((sum, s) => sum + (s.total || 0), 0);
  const salesCount   = displaySales.length;
  const highestSale  = salesCount ? Math.max(...displaySales.map(s => s.total || 0)) : 0;

  // Wunschpreis-Erfüllung: only for inventory (non-spontan, non-bundle) sales
  const inventorySales = displaySales.filter(s => {
    const items = s.sale_items || [];
    return !s.is_bundle && items.length > 0 && !!items[0]?.item_id;
  });
  const actualSum = inventorySales.reduce((sum, s) => sum + (s.total || 0), 0);
  const wishSum   = inventorySales.reduce((sum, s) => {
    const wp = s.sale_items[0]?.wish_price ?? s.sale_items[0]?.unit_price ?? 0;
    return sum + wp;
  }, 0);
  const wunschPct = (wishSum > 0 && inventorySales.length > 0)
    ? Math.round((actualSum / wishSum) * 100)
    : null;

  // ── Date label ─────────────────────────────────────────────────────────────
  const dateLabel = market.date
    || (market.starts_at
      ? new Date(market.starts_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : 'Markttag');

  // ── Standgebühr-Edit ─────────────────────────────────────────────────────────
  const openFeeEdit = (el) => {
    scrollTargetRef.current = el ?? scrollTargetRef.current;
    const seed = boothFee ?? 0;
    setFeeSeed(seed);
    setDraftFee(seed);
    setClosingPad(false);
    setPadEntered(false);
    setPadSession((s) => s + 1);
    setFeeEditOpen(true);
  };
  // NumPad schließen: max-height kollabiert nach unten, dann unmount.
  const closeNumpad = () => {
    if (!feeEditOpen) return;
    setClosingPad(true);
    setFeeEditOpen(false);
    setTimeout(() => setClosingPad(false), 320);
  };
  const writeActive = (v) => { if (feeEditOpen) setDraftFee(v); };
  const saveFee = async () => {
    const ok = await onUpdateBoothFee?.(market.id, draftFee);
    if (ok !== false) { setBoothFee(draftFee); closeNumpad(); }
  };
  const removeFee = async () => {
    const ok = await onUpdateBoothFee?.(market.id, null);
    if (ok !== false) { setBoothFee(null); closeNumpad(); }
  };

  // Live-Wert in der Standgebühr-Zeile: beim Bearbeiten der laufende Draft, sonst der committete Wert.
  const feeForDisplay = feeEditOpen ? draftFee : boothFee;
  const padMounted = feeEditOpen || closingPad;

  const fee = feeForDisplay || 0;
  const gewinn = totalRevenue - fee;

  return (
    <SheetShell
      open={open}
      onClose={() => onOpenChange(false)}
      grouped
      testId="markt-detail-sheet"
      backdropTestId="markt-detail-backdrop"
      scrollTestId="markt-detail-scroll"
      title={dateLabel}
      subtitle={
        <span className="flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{market.location || market.name || '\u2014'}</span>
        </span>
      }
      rawFooter={padMounted && (
        <div
          ref={footerRef}
          data-testid="markt-detail-fee-numpad"
          className="shrink-0 overflow-hidden px-4"
          style={{
            maxHeight: closingPad ? '0px' : (padH ? `${padH}px` : '600px'),
            transform: padEntered ? 'translateY(0)' : 'translateY(100%)',
            transition: `transform 0.3s ${IOS_EASE}, max-height 0.3s ${IOS_EASE}`,
            boxShadow: 'inset 0 0.5px 0 var(--separator)',
          }}
        >
          <div ref={contentRef} className="pb-3 pt-1.5">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={removeFee}
                className="h-11 px-1 text-[17px] text-[var(--accent-red)] active:opacity-60"
              >
                Entfernen
              </button>
              <button
                type="button"
                data-testid="markt-detail-fee-save"
                onClick={saveFee}
                className="h-11 px-1 text-[17px] font-semibold text-[var(--accent-troedel)] active:opacity-60"
              >
                Fertig
              </button>
            </div>
            <SaleNumpad key={padSession} hideDisplay prefill={feeSeed} onValueChange={writeActive} />
          </div>
        </div>
      )}
    >
      {/* Umsatz als Hero, Kennzahlen als ruhige Liste (statt Kachel-Raster) */}
      <div data-testid="stat-tile-markt-umsatz" className="px-1 pb-4 pt-1">
        <p className="text-[13px] text-[var(--label-secondary)]">Umsatz</p>
        <p className="num text-[40px] font-bold leading-[1.05] tracking-[-0.02em]">{fmtCurrency(totalRevenue)}</p>
      </div>

      <InsetGroup className="mb-6" surface="L2">
        <InsetRow first testId="stat-tile-markt-verkaeufe" title="Verkäufe" value={salesCount} valueClassName="font-semibold" />
        <InsetRow testId="stat-tile-markt-bester" title="Bester Verkauf" value={fmtCurrency(highestSale)} valueClassName="font-semibold" />
        <InsetRow
          testId="stat-tile-markt-wunsch"
          title="Wunschpreis erreicht"
          subtitle={wunschPct != null ? null : 'Keine Sortiments-Verkäufe'}
          value={wunschPct != null ? `${wunschPct}\u202F%` : '\u2014'}
          valueClassName="font-semibold"
        />
      </InsetGroup>

      {/* Standgebühr / Gewinn — Wert läuft beim Bearbeiten live mit (NumPad im Footer) */}
      <div ref={scrollTargetRef}>
        <InsetGroup
          title="Standgebühr"
          className="mb-6"
          surface="L2"
          footer={(feeEditOpen || boothFee != null) ? null : 'Mit Standgebühr siehst du den Gewinn dieses Markttags.'}
        >
          <div
            className="rounded-[16px]"
            style={{ boxShadow: feeEditOpen ? 'inset 0 0 0 2px var(--accent-troedel)' : 'none', transition: 'box-shadow 150ms var(--ease-out-ios)' }}
          >
            <InsetRow
              first
              title="Standgebühr"
              value={(feeEditOpen || boothFee != null)
                ? <span data-testid="markt-detail-standgebuehr" className="tabular-nums">{fmtCurrency(feeForDisplay)}</span>
                : <span className="text-[var(--label-secondary)]">Keine</span>}
              trailing={!feeEditOpen && (
                <button
                  type="button"
                  data-testid="markt-detail-fee-edit"
                  onClick={(e) => openFeeEdit(e.currentTarget.closest('section'))}
                  className="-mr-1 flex h-11 items-center gap-1.5 rounded-full px-3 text-[15px] font-semibold text-[var(--accent-troedel)] active:bg-[var(--fill-quaternary)]"
                >
                  <Pencil className="h-4 w-4" />
                  {boothFee == null ? 'Hinzufügen' : 'Ändern'}
                </button>
              )}
            />
            {(feeEditOpen || boothFee != null) && (
              <InsetRow
                title={<span className="font-semibold">Gewinn</span>}
                value={
                  <span
                    data-testid="markt-detail-gewinn"
                    className="font-bold tabular-nums"
                    style={{ color: gewinn >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}
                  >
                    {fmtCurrency(gewinn)}
                  </span>
                }
              />
            )}
          </div>
        </InsetGroup>
      </div>

      {/* Verkäufe */}
      <InsetGroup title="Verkäufe" aside={salesCount || null} className="pb-6" surface="L2">
        {loading ? (
          [0, 1, 2].map(i => (
            <div key={i} className={`mx-4 h-[52px] animate-pulse ${i ? 'border-t border-[var(--separator)]' : ''}`}>
              <div className="mt-[18px] h-4 w-2/3 rounded bg-[var(--fill-tertiary)]" />
            </div>
          ))
        ) : displaySales.length > 0 ? (
          displaySales.map((sale, idx) => {
            const saleItems = sale.sale_items || [];
            const primary   = saleItems[0];
            const isBundle  = sale.is_bundle && saleItems.length > 1;
            const isSpontan = saleItems.length > 0 && !primary?.item_id;

            const label = isBundle
              ? `Bundle (${saleItems.length}\u202FArtikel)`
              : (primary?.item_name || 'Spontan-Verkauf');

            const wishPrice = (!isBundle && !isSpontan && primary?.wish_price != null && primary.wish_price !== sale.total)
              ? primary.wish_price
              : null;

            return (
              <div key={sale.id ?? idx} data-testid="sale-row" className="flex min-h-[52px] items-center gap-3 pl-4 pr-4">
                <div className={`flex min-h-[52px] min-w-0 flex-1 items-center gap-3 py-2 ${idx ? 'border-t border-[var(--separator)]' : ''}`}>
                  {(isBundle || isSpontan) && (
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--tint-troedel)]">
                      {isBundle
                        ? <Package className="h-3.5 w-3.5 text-[var(--accent-troedel)]" />
                        : <Zap className="h-3.5 w-3.5 text-[var(--accent-troedel)]" />}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[17px] leading-tight">{label}</p>
                    {wishPrice != null && (
                      <p className="num mt-0.5 text-[13px] text-[var(--label-secondary)]">
                        Wunsch: {fmtCurrency(wishPrice)}
                      </p>
                    )}
                  </div>
                  <span className="num shrink-0 text-[17px] font-bold">
                    {fmtCurrency(sale.total)}
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <p className="px-4 py-5 text-center text-[15px] text-[var(--label-secondary)]">
            Keine Verkaufsdaten vorhanden.
          </p>
        )}
      </InsetGroup>
    </SheetShell>
  );
}
